/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-10-24 14:51:05                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-21 14:35:16                               *
 *****************************************************************************/

/* SUMMARY
 * Imports
 * Interfaces
 * Do request with token
 * Get request
 * Put request
 * Patch request
 * Post request
 * Delete request
 */

/* Imports */
import { env } from "../../env";
import { initKeycloak } from "../auth/keycloak";
import { normalizeApiErrorFromResponse } from "./error";
/***/

/* Interfaces */
interface IApiOptions {
  host?: string;
  preUrl?: string;
  responseType?: "arraybuffer";
  isMultipart?: boolean;
  headers?: Record<string, string>;
}
/***/

class Api {
  initToken = (): string | undefined => {
    const token = initKeycloak().token;
    // Stockage de l'ID utilisateur (sub) dans le localStorage
    if (token && typeof token === "string") {
      try {
        const payload = token.split(".")[1];
        const decodedPayload = JSON.parse(atob(payload));
        const userId = decodedPayload.sub;
        if (userId) {
          localStorage.setItem("userId", userId);
        }
      } catch (error) {
        console.error("Erreur lors du décodage du token:", error);
      }
      return token;
    }
  };

  /**
   * Do request with token
   * @param method - Request method
   * @param url - Request path
   * @param data - Request payload if needed
   */
  request = (
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    url: string,
    data?: unknown,
    options?: IApiOptions
  ): Promise<unknown> => {
    const realm = localStorage.getItem("realm");

    // Récupération du token
    const token = this.initToken();

    const headers = new Headers({
      Accept: "*/*",
    });

    if (token) headers.append("Authorization", `Bearer ${token}`);

    if (!options?.isMultipart) {
      headers.set("Content-Type", "application/json");
    }

    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        headers.set(key, value);
      });
    }

    const host = options?.host || env.GATEWAY_URL; // Overwrite host by host provided by options
    const preUrl = options?.preUrl !== undefined ? options.preUrl : `/${realm}`;

    return new Promise((resolve, reject) => {
      try {
        fetch(`${host}${preUrl}/${url}`, {
          method: method,
          headers: headers,
          body: options?.isMultipart
            ? (data as BodyInit)
            : data
              ? JSON.stringify(data)
              : undefined,
        })
          .then(async (ret) => {
            if (!ret.ok) {
              const contentType = ret.headers.get("content-type");
              const isJson = contentType && contentType.includes("application/json");
              let body;

              try {
                if (isJson) {
                  body = await ret.json();
                } else {
                  const textBody = await ret.text();
                  // Try to parse as JSON even if content-type is not application/json
                  try {
                    body = JSON.parse(textBody);
                  } catch {
                    body = textBody;
                  }
                }
              } catch (parseError) {
                body = undefined;
              }

              const apiErr = normalizeApiErrorFromResponse(ret, body);
              throw apiErr;
            }

            if (options?.responseType === "arraybuffer") {
              return resolve(ret.arrayBuffer());
            }

            const contentType = ret.headers.get("content-type");
            const isJsonResponse =
              contentType && contentType.includes("application/json");

            // Vérification du corps de la réponse
            if (ret.status === 200 && isJsonResponse) {
              try {
                const json = await ret.json();
                return resolve(json);
              } catch {
                console.warn("Response body is empty, returning empty object.");
                return resolve({});
              }
            } else {
              // Pas de contenu JSON
              return resolve({});
            }
          })
          .catch((err) => {
            reject(err);
          });
      } catch (err) {
        reject(err);
      }
    });
  };
  /***/

  /**
   * Get request
   * @param url - Request path
   */
  get = (url: string, options?: IApiOptions): Promise<unknown> => {
    return this.request("GET", url, undefined, options);
  };
  /***/

  /**
   * Get request photos with arrayBuffer responseType: 'arraybuffer'
   * @param url - Request path
   */
  getPhotos = async (
    url: string,
    options?: IApiOptions
  ): Promise<ArrayBuffer> => {
    return this.request("GET", url, undefined, {
      ...options,
      responseType: "arraybuffer",
    }) as Promise<ArrayBuffer>;
  };
  /***/

  /**
   * Put request
   * @param url - Request path
   * @param data - Request payload
   */
  put = (
    url: string,
    data: unknown,
    options?: IApiOptions
  ): Promise<unknown> => {
    return this.request("PUT", url, data, options);
  };
  /***/

  /**
   * Patch request
   * @param url - Request path
   * @param data - Partial payload: only the fields to change
   */
  patch = (
    url: string,
    data: unknown,
    options?: IApiOptions
  ): Promise<unknown> => {
    return this.request("PATCH", url, data, options);
  };
  /***/

  /**
   * Post request
   * @param url - Request path
   * @param data - Request payload
   */
  post = (
    url: string,
    data: unknown,
    options?: IApiOptions
  ): Promise<unknown> => {
    return this.request("POST", url, data, options);
  };
  /***/

  /**
   * Delete request
   * @param url - Request path
   */
  delete = (url: string, options?: IApiOptions): Promise<unknown> => {
    return this.request("DELETE", url, options);
  };
  /***/
}

const apiSingleton = () => {
  return new Api();
};

declare global {
  var api: undefined | ReturnType<typeof apiSingleton>;
}

export const api = globalThis.api ?? apiSingleton();
